/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { Button } from '@az/starc-ui';
import { type ParentSkuCardProps } from '../../ParentSkuCard';
import styles from './VehicleNotFitV2.module.scss';
import { Icon } from '@az/starc-ui';
import { NoFit } from '@az/starc-ui-icons';
import Image from '@/components/NextImage';
import { getPriceArray } from '../../../utils/getPriceArray';
import Price from '@/components/AZCustomComponent/Price';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { usePageType } from '@/features/category/api/getPageType';
import { useLabels } from '@/hooks/useLabels';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useMemo, useCallback } from 'react';
import { getFittingPartsURL } from '../../../utils/getFittingPartsURL';

type Props = {
  fittingPartsURL: string;
  parentSkuCardProps: ParentSkuCardProps;
  onVehicleDoesNotFitCtaOnClick: () => void;
};

const labelMap = {
  label_deal_bundle_parent_sku_quantity: 'label_deal_bundle_parent_sku_quantity',
  label_deal_vehicle_not_fit_heading: 'label_deal_vehicle_not_fit_heading',
  label_deal_vehicle_not_fit_message: 'label_deal_vehicle_not_fit_message',
  label_deal_vehicle_not_fit_cta: 'label_deal_vehicle_not_fit_cta',
};

export function VehicleNotFitV2({
  fittingPartsURL,
  parentSkuCardProps,
  onVehicleDoesNotFitCtaOnClick,
}: Props) {
  const labels = useLabels(labelMap);
  const {
    skuDetails: { productImageUrl, description },
    skuPrice,
    skuRetailPrice,
    skuQuantity,
  } = parentSkuCardProps;
  const { acesEnabled } = useAcesFlags();
  const { data: headerData } = useHeaderData();
  const { data: pageTypeData } = usePageType();
  const preferredVehicle = getPreferredVehicle(headerData);
  const vehicle = preferredVehicle || pageTypeData;
  const selectedVehicle = vehicle
    ? [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
    : '';

  const finalFittingPartsURL = useMemo(
    () => getFittingPartsURL(fittingPartsURL, acesEnabled),
    [acesEnabled, fittingPartsURL],
  );

  const handleClick = useCallback(() => {
    onVehicleDoesNotFitCtaOnClick();
  }, [onVehicleDoesNotFitCtaOnClick]);

  const messageParts = useMemo(() => {
    const message = labels.label_deal_vehicle_not_fit_message;
    const parts = message.split('${vehicle}');

    if (parts.length !== 2) {
      return { textBeforeVehicle: message, textAfterVehicle: '' };
    }

    const [textBeforeVehicle, textAfterVehicle] = parts;
    return { textBeforeVehicle, textAfterVehicle };
  }, [labels.label_deal_vehicle_not_fit_message]);

  return (
    <section className={styles.root} aria-labelledby="vehicle-not-fit-heading">
      <div className={styles.information}>
        <div className={styles.alert} role="alert" aria-live="polite">
          <Icon svg={NoFit} size={5} aria-hidden="true" />
          <h2 id="vehicle-not-fit-heading">{labels.label_deal_vehicle_not_fit_heading}</h2>
        </div>
        <p className={styles.message}>
          {messageParts.textBeforeVehicle}
          <strong>{selectedVehicle}</strong>
          {messageParts.textAfterVehicle}
        </p>
      </div>

      <article className={styles.card} aria-label="Product details">
        <div className={styles.cardBody}>
          <Image src={productImageUrl} alt={description} width={64} height={64} />
          <div className={styles.product}>
            <strong className={styles.title}>{description}</strong>
            <div>
              <span className={styles.parentSkuQuantityLabel}>
                {labels.label_deal_bundle_parent_sku_quantity}{' '}
              </span>
              <span className={styles.parentSkuQuantityValue}>{skuQuantity}</span>
            </div>
          </div>
          <div className={styles.price}>
            {skuPrice ? (
              <Price
                priceType="small"
                dealPrice={getPriceArray(skuPrice)}
                wasDealPrice={getPriceArray(skuRetailPrice)}
                each={+skuQuantity > 1}
                priceClassName={styles.price}
                centsClassName={styles.cents}
              />
            ) : (
              <Price
                priceType="small"
                dealPrice={getPriceArray(skuRetailPrice)}
                each={+skuQuantity > 1}
                priceClassName={styles.price}
                centsClassName={styles.cents}
              />
            )}
          </div>
        </div>
      </article>

      <Button className={styles.button} href={finalFittingPartsURL} onClick={handleClick}>
        {labels.label_deal_vehicle_not_fit_cta}
      </Button>
    </section>
  );
}
